import styled, { createGlobalStyle } from 'styled-components';
import { baseVariables } from '@marriott/mi-ui-library';

interface ChatMapMarkerStylesProps {
  $clusterHoverIconUrl: string;
  $aiSparkleIconUrl: string;
}

export const ChatMapMarkerStyles = createGlobalStyle<ChatMapMarkerStylesProps>`
  /* Single property view marker - circle dot instead of a price/title pin.
     Black background by default, white (with border) on hover/active.
     Uses the doubled class to out-specify the later .m-map-pin base rule. */
  .m-map-pin.m-map-pin-circle {
    width: 18px !important;
    height: 18px !important;
    min-width: 18px !important;
    padding: 0 !important;
    gap: 0 !important;
    border-radius: 50% !important;
    background-color: ${baseVariables.color['base10']} !important;
    border: 2px solid ${baseVariables.color['base20']} !important;
    box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.4) !important;
    /* Hide the truncated title text - this marker is purely a dot */
    color: transparent !important;
    font-size: 0 !important;
    overflow: hidden !important;
  }

  /* Remove the rectangular pin's pointer tail for the circle variant.
     Use the doubled class + cover active state so it stays hidden when selected. */
  .m-map-pin.m-map-pin-circle:after,
  .m-map-pin.m-map-pin-circle.active:after,
  .m-map-pin.m-map-pin-circle:hover:after {
    display: none !important;
    content: none !important;
  }

  /* Hover / active state - white circle with dark border.
     Combine both classes to out-specify the later .m-map-pin.active rule. */
  .m-map-pin.m-map-pin-circle:hover,
  .m-map-pin.m-map-pin-circle.active {
    background-color: ${baseVariables.color['base20']} !important;
    border: 2px solid ${baseVariables.color['base10']} !important;
  }

  /* Base marker pin styling - updated per Figma design */
  .m-map-pin {
    background-color: #1c1c1c !important;
    border-radius: 4px !important;
    box-shadow: 0px 4px 14px 0px rgba(0, 0, 0, 0.4) !important;
    color: #fff !important;
    cursor: pointer !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 2px !important;
    font-size: 14px !important;
    line-height: 1.5 !important;
    height: 20px !important;
    padding: 0 4px !important;
    position: relative !important;
    text-align: center !important;
    white-space: nowrap !important;
    font-family: var(--t-font-family, Swiss-721) !important;
    font-weight: 500 !important;
    pointer-events: auto !important;
    transition: background-color 0.15s ease, color 0.15s ease !important;
    z-index: 100 !important;
  }

  .m-map-pin:after {
    border: 5px solid transparent !important;
    border-top-color: #1c1c1c !important;
    content: "" !important;
    left: 50% !important;
    margin-left: -5px !important;
    position: absolute !important;
    top: 100% !important;
  }

  .m-map-pin.active {
    background-color: #fff !important;
    border: 1px solid #c4c4c4 !important;
    color: #1c1c1c !important;
  }

  .m-map-pin.active:after {
    border-top-color: #fff !important;
  }

  /* Hover state - same as active state per Figma */
  .m-map-pin:hover {
    background-color: #fff !important;
    color: #1c1c1c !important;
  }

  .m-map-pin:hover:after {
    border-top-color: #fff !important;
  }

  /* Ensure marker label container allows pointer events */
  .gm-style div[style*="cursor: pointer"] .m-map-pin,
  .gm-style div[role="button"] .m-map-pin {
    pointer-events: auto !important;
  }

  /* Marker label styling for unavailable rates - same as shop */
  .marker-label-grey {
    background-color: #707070 !important;
    color: #fff !important;
  }

  .marker-label-grey:after {
    border-top-color: #707070 !important;
  }

  /* AI recommended marker - shows sparkle icon before price */
  .m-map-pin.ai-recommended::before {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    margin-right: 2px;
    background-image: url('${props => props.$aiSparkleIconUrl}');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    vertical-align: middle;
    flex-shrink: 0;
  }

  /* Never show the sparkle inside the single-property circle dot */
  .m-map-pin.m-map-pin-circle.ai-recommended::before {
    display: none !important;
    content: none !important;
  }

  /* Cluster marker hover styles - CSS only, no JS timing issues */
  div.cluster {
    cursor: pointer !important;
    z-index: 500 !important;
    position: relative !important;
    pointer-events: auto !important;
  }

  div.cluster > img {
    pointer-events: none !important;
    transition: opacity 0.15s ease !important;
  }

  /* On hover, hide the original icon */
  div.cluster:hover > img {
    opacity: 0 !important;
  }

  /* Show hover icon via pseudo-element */
  div.cluster::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 56px;
    height: 56px;
    background-image: url('${props => props.$clusterHoverIconUrl}');
    background-size: contain;
    opacity: 0;
    transition: opacity 0.15s ease;
    pointer-events: none;
  }

  div.cluster:hover::before {
    opacity: 1;
  }

  /* Text color change on hover */
  div.cluster > div {
    transition: color 0.15s ease !important;
  }

  div.cluster:hover > div {
    color: #1c1c1c !important;
  }

  /* Programmatic cluster hover state (triggered from property name hover) */
  div.cluster.cluster-hover-active > img {
    opacity: 0 !important;
  }

  div.cluster.cluster-hover-active::before {
    opacity: 1;
  }

  div.cluster.cluster-hover-active > div {
    color: #1c1c1c !important;
  }

  /* Fix z-index for fee modal to appear above MMapPinHoverOverlay (z-index: 1400).
     The Modal component portals to document.body with z-index: 1011, which is lower
     than the hover overlay. This override ensures the modal appears on top. */
  body > [data-component-name="m-ui-library-Modal"] {
    z-index: 1500 !important;
  }
`;

/**
 * Outer wrapper for the chat map view.
 *
 * IMPORTANT: This element is the portal target for `MSmartInfoWindow`.
 * It must remain `position: relative` so the portaled InfoWindow can be
 * positioned absolutely relative to it and sit in the same stacking
 * context as the filter chips and mobile list toggle.
 */
export const ChatMapContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;

  .gm-style {
    font-family: Swiss-721, Arial, Helvetica, sans-serif;
  }

  .gm-fullscreen-control {
    display: none !important;
  }

  .gm-style-mtc {
    display: none;
  }

  .gm-style-iw.gm-style-iw-c {
    padding: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    max-height: unset !important;
    border-radius: 0 !important;

    .gm-style-iw-d {
      overflow: visible !important;
      padding: 0;
      max-height: 100% !important;
    }

    /* Hide the default close button - we handle close via onCloseClick */
    button.gm-ui-hover-effect {
      top: 16px !important;
      right: 16px !important;
      width: 32px !important;
      height: 32px !important;
      background: rgba(0, 0, 0, 0.45) !important;
      backdrop-filter: blur(12.5px) !important;
      border-radius: var(--radius-borderradius-m, 8px) !important;
      opacity: 1 !important;

      span {
        background-color: #fff !important;
        width: 16px !important;
        height: 16px !important;
        margin: 8px !important;
      }
    }
  }

  /* Remove the default InfoWindow tail/arrow */
  .gm-style-iw-tc {
    display: none !important;
  }

  /* Hide zoom controls on mobile */
  @media (max-width: 767px) {
    .gm-bundled-control,
    .gmnoprint[role='menubar'],
    .gm-style > div > div > div > div > div[style*='position: absolute'][style*='right'] {
      display: none !important;
    }
  }
`;

export const DotsLoaderOverlay = styled.div`
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
`;

export const DotsLoaderPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 18px;
  background-color: #ffffff;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

export const DotsLoaderDot = styled.div<{ $delay?: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #b0b0b0;
  animation: dotPulse 1.4s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay || '0s'};

  @keyframes dotPulse {
    0%,
    100% {
      background-color: #b0b0b0;
      transform: scale(1);
    }
    50% {
      background-color: #707070;
      transform: scale(1.3);
    }
  }
`;

export const HoverPricePin = styled.div`
  background-color: var(--t-brand-bg-color, #fff);
  color: var(--t-brand-fg-color, #1c1c1c);
  border-radius: 4px;
  box-shadow: 0px 4px 14px 0px rgba(0, 0, 0, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 14px;
  line-height: 1.5;
  height: 20px;
  padding: 0 4px;
  position: relative;
  text-align: center;
  white-space: nowrap;
  font-family: var(--t-font-family, Swiss-721);
  font-weight: 700;

  &:after {
    border: 5px solid transparent;
    border-top-color: var(--t-brand-bg-color, #fff);
    content: '';
    left: 50%;
    margin-left: -5px;
    position: absolute;
    top: 100%;
  }

  &.marker-label-grey {
    background-color: #707070;
    color: #fff;

    &:after {
      border-top-color: #707070;
    }
  }
`;

export const MapInfoWindowContent = styled.div`
  width: 304px;
  background: var(--colors-base-20, #fff);
  border-radius: var(--general-spacer-03, 16px);
  overflow: hidden;
  cursor: pointer;
  padding: var(--general-spacer-02, 8px);
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.07);

  /* Override PropertyCardComponent styles for map info window (desktop) */
  .property-card-container {
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    border: none !important;
  }

  /* Image carousel styling */
  .image-gallery-container,
  .m-image-carousel,
  [class*='imageCarousel'],
  [class*='ImageCarousel'] {
    border-radius: 8px !important;
    overflow: hidden !important;
    aspect-ratio: 284 / 160;
  }

  /* Content wrapper styling */
  .text-container,
  [class*='contentWrapper'],
  [class*='ContentWrapper'] {
    padding: 0 var(--general-spacer-01, 4px) !important;
  }

  /* Hotel description wrapper */
  .details-container,
  [class*='hotelDescription'],
  [class*='HotelDescription'] {
    padding: var(--general-spacer-02, 8px) 0 !important;
    margin: 0 !important;
    gap: 8px;
  }

  /* AI best match tag styling */
  [class*='tag'],
  [class*='Tag'],
  [class*='badge'],
  [class*='Badge'] {
    background: var(--color-set-brand-bg-color, #fff);
    border: 1px solid #ffeee9;
    border-radius: 4px;
    padding: 0 var(--general-spacer-02, 8px);
    height: 22px;
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.07);
  }

  /* Property title styling */
  [class*='propertyName'],
  [class*='PropertyName'],
  [class*='hotelName'],
  [class*='HotelName'],
  .property-title {
    font-family: var(--typography-subtitle-font-family, 'Swiss721 BT', sans-serif);
    font-weight: var(--typography-subtitle-xl-font-weight, 500);
    font-size: 16px;
    line-height: 1.25;
    color: var(--colors-base-10, #1c1c1c);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Rating and distance row */
  [class*='rating'],
  [class*='Rating'],
  [class*='distance'],
  [class*='Distance'] {
    font-family: 'Swiss721 BT', sans-serif;
    font-size: 12px;
    line-height: 1.5;
    color: var(--colors-neutral-40, #707070);
  }

  /* Rate bar styling */
  [class*='rateBar'],
  [class*='RateBar'],
  [class*='priceContainer'],
  [class*='PriceContainer'] {
    border-top: 1px solid var(--colors-neutral-20, #eee);
    padding-top: var(--general-spacer-02, 8px) !important;
    gap: var(--general-spacer-03, 16px);
  }

  /* Price styling */
  [class*='price']:not(.m-price-currency-s),
  [class*='Price']:not(.m-price-currency-s),
  [class*='rate'],
  [class*='Rate'] {
    font-family: var(--typography-subtitle-font-family, 'Swiss721 BT', sans-serif);
    font-weight: var(--typography-subtitle-xl-font-weight, 500);
    font-size: var(--typography-t-subtitle-xl-font-size, 22px);
    line-height: 1.25;
    color: var(--colors-base-10, #1c1c1c);
  }

  /* Per night label */
  [class*='perNight'],
  [class*='PerNight'],
  [class*='nightLabel'],
  [class*='NightLabel'] {
    font-family: 'Swiss721 BT', sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 1;
    color: var(--colors-neutral-40, #707070);
  }

  /* Hide view rates button in map info window */
  .view-rates-button,
  .view-hotel-details-section {
    display: none !important;
  }

  /* Favorite button styling */
  [class*='favorite'],
  [class*='Favorite'] {
    background: rgba(28, 28, 28, 0.5);
    border-radius: var(--general-spacer-01, 4px);
    width: var(--general-spacer-05, 32px);
    height: var(--general-spacer-05, 32px);
  }

  /* Arrow button styling */
  [class*='actionIcon'],
  [class*='ActionIcon'],
  [class*='arrowButton'],
  [class*='ArrowButton'] {
    border-radius: var(--general-spacer-01, 4px);
    width: 32px;
    height: 32px;
  }
`;

/* Mobile bottom sheet card - positioned above list button with gap */
export const MobileBottomCard = styled.div`
  position: absolute;
  bottom: calc(env(safe-area-inset-bottom) + 100px);
  left: 0;
  right: 0;
  z-index: 9;
  display: flex;
  justify-content: center;
  cursor: pointer;

  /* Hide view rates button in bottom sheet */
  .view-rates-button-container,
  .view-rates-button {
    display: none !important;
  }
`;

export const MobileBottomCardClose = styled.button`
  display: none;
`;
